import IAppLogger from '@app/lib/IAppLogger';
import { ChannelClassificationInput } from '@api/generated/graphql';
import { classifyLiveServicesOriginCall } from '@modules/InboxModule/mutations/classifyLiveServicesOriginCall';
import TasksModule from '@modules/TasksModule';
import { ITask } from '@modules/TasksModule/types';

/**
 * Copies the agent's classification onto the customer's call (leg 1) that
 * claimLiveServicesCall stashed on the task, with the tenant it was claimed
 * in. Only a Live Services claim stashes one, so a task without it is skipped.
 * Takes the same classification leg 2 was just written with, so the BFF can
 * write leg 1 the way the interactions service writes leg 2. Fire-and-forget:
 * leg 2 is already classified, and closing the task never waits on this.
 *
 * Not flag-gated here: the rollout flag is targeted on the customer's tenant,
 * which only the BFF evaluates, so it decides and answers false when off.
 */
export const classifyLiveServicesOriginCallForTask = (
  task: ITask,
  logger: IAppLogger,
  classification: ChannelClassificationInput,
) => {
  // The claim can land after the modal rendered, so read the task as it is now.
  const { attributes } = TasksModule.getTask(task.id) ?? task;
  const callId: string | undefined = attributes.liveServicesOriginalCallId;
  const tenantId: string | undefined =
    attributes.liveServicesOriginalCallTenantId;

  if (!callId || !tenantId || !classification.reasonId) {
    return;
  }

  const note = classification.initialDispositionNote;

  classifyLiveServicesOriginCall({
    tenantId,
    callId,
    classification: {
      ...classification,
      // A blank note would wipe the memo already on leg 1.
      initialDispositionNote: note?.trim() ? note : null,
    },
  })
    .then((classified) => {
      logger.info('live-services:origin-call-classification', {
        taskId: task.id,
        callId,
        classified,
      });
    })
    .catch((error) => {
      logger.exception(error, 'Failed to classify Live Services origin call', {
        taskId: task.id,
        tenantId,
        callId,
      });
    });
};
